import { GroupsDemoEmptyPage } from "@components/common/empty-page/groups-demo-empty-page";
import { GroupsEmptyPage } from "@components/common/empty-page/groups-empty-page";
import { PageContainer } from "@components/common/page-container";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { DEFAULT_GROUPS_SORT_BY } from "@components/groups/groups-constants";
import { GroupsSort } from "@components/groups/groups-sort";
import { BulkExportGroups } from "@components/table/groups/bulk-actions/bulk-export-groups";
import { GroupsSortBy } from "@custom-types/group-types";
import { isGroupsSortBy } from "@custom-types/type-guards";
import { useGroups } from "@hooks/use-groups";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { GroupsActionButtons } from "@pages/groups/groups-action-buttons";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useMediaQueryList } from "@hooks/use-media-query";
import { GroupsMobileView } from "@pages/groups/groups-mobile-view";
import {
  fetchingGroupsFlagsSelector,
  filteredCompanyGroupsSelector,
} from "@store/groups/groups-selector";
import { useAppSelector } from "@store/store-helper";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { isGroupDemo, sortGroups } from "@utils/group-utils";
import { useMemo } from "react";
import { GroupsPageTable } from "src/pages/groups/groups-page-table";
import { GroupsTiles } from "src/pages/groups/groups-tiles";

/** Content in the Groups page */
export function GroupsPage(): JSX.Element {
  const companyId = useCompanyId();
  const groups = useGroups();
  const filteredCompanyGroups = useAppSelector(filteredCompanyGroupsSelector);
  const { groups: viewMode } = useAppSelector(dataViewModeSelector);
  const { isFetchingGroups } = useAppSelector(fetchingGroupsFlagsSelector);
  const { sortBy, setSortBy } = useSubjectsSortByQuery<GroupsSortBy>({
    defaultSort: DEFAULT_GROUPS_SORT_BY,
    isValidSort: isGroupsSortBy,
  });
  const { isScreenSmOrSmaller } = useMediaQueryList();

  // Sort the filtered groups client-side
  const sortedGroups = useMemo(
    () => sortGroups(filteredCompanyGroups, sortBy),
    [filteredCompanyGroups, sortBy]
  );
  if (
    groups.length === 1 &&
    isGroupDemo(groups[0]) &&
    filteredCompanyGroups.length === 1
  ) {
    return (
      <GroupsDemoEmptyPage
        companyId={companyId}
        demoGroup={filteredCompanyGroups[0]}
      />
    );
  }

  const shouldShowEmptyPage =
    filteredCompanyGroups.length === 0 && !isFetchingGroups;

  let groupsContent = (
    <GroupsPageTable
      companyId={companyId}
      groups={sortedGroups}
      isLoading={isFetchingGroups}
      sortBy={sortBy}
      setSortBy={setSortBy}
    />
  );

  if (viewMode === "cards" && !isScreenSmOrSmaller) {
    groupsContent = <GroupsTiles groups={sortedGroups} />;
  }

  const shouldRenderActions = sortedGroups.length > 0;

  return (
    <PageContainer
      title="Groups Management"
      dataTestId="GROUPS_CONTAINER"
      actionButtons={
        isScreenSmOrSmaller ? undefined : (
          <GroupsActionButtons groups={groups} />
        )
      }
    >
      <EntitiesToolbar
        toolbarEntityName="Group"
        numberOfRows={filteredCompanyGroups.length}
        isLoading={isFetchingGroups}
        sortActions={
          shouldRenderActions && (
            <GroupsSort
              key="groups-sort"
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
          )
        }
        exportComponent={
          shouldRenderActions && (
            <BulkExportGroups
              key="BulkExportGroups"
              selectedGroups={groups}
              /* It is false since entities can't be selected in tiles view */
              areEntitiesSelected={false}
              buttonType="standard"
            />
          )
        }
      />

      {/* If empty page exist, show it. If not show the content */}
      {shouldShowEmptyPage ? (
        <GroupsEmptyPage />
      ) : (
        <>
          {isScreenSmOrSmaller && (
            <GroupsMobileView
              groups={sortedGroups}
              sortBy={sortBy}
              isLoading={isFetchingGroups}
            />
          )}

          {!isScreenSmOrSmaller && groupsContent}
        </>
      )}
    </PageContainer>
  );
}
